import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import { API_BASE_URL } from '../config/ApiConfig';
import PaginationBar from './components/PaginationBar';
import TableContainer from './components/TableContainer';
import DataTable from './components/DataTable';
import ResponsiveShadow from './components/ResponsiveShadow';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import ConfirmModal from './components/modals/ConfirmModal';
import RendimentoModal from './components/modals/RendimentoModal';
import RendimentosJsonUploadModal from './components/modalServices/RendimentosJsonUploadModal';
import usePagination10 from './components/hooks/usePagination10';
import useReferenceData from './components/hooks/useReferenceData';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import Toolbar from './components/Toolbar';
import { formatCurrency, fmtCurrency } from './utils/formatMoeda';
import LoadingModal from './components/modals/LoadingModal';

const emptyForm = {
  ano: '', idPrograma: '', idEscola: '', idConta: '',
  janeiro: '', fevereiro: '', marco: '', abril: '', maio: '', junho: '',
  julho: '', agosto: '', setembro: '', outubro: '', novembro: '', dezembro: ''
};

function Rendimentos() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const isGestor = JSON.parse(localStorage.getItem('vinculo') || '{}')?.tipo === 'GEST';
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ ...emptyForm, idEscola: cnpj || '', ano: ano || '' });
  const [showModal, setShowModal] = useState(false);
  const [showJsonUploadModal, setShowJsonUploadModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [atualizar, setAtualizar] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();
  const { setPagination } = usePagination10(items.length);
  const { programas, contas } = useReferenceData(token, cnpj);
  function parseContaNum(idConta) {
    const m = String(idConta).match(/^\d+/);
    return m ? parseInt(m[0], 10) : 0;
  }
  const [filtroPrograma, setFiltroPrograma] = useState('');
  const itemsOrdenados = [...items].sort((a, b) => {
    const nA = parseContaNum(a.idConta);
    const nB = parseContaNum(b.idConta);
    if (nA !== nB) return nA - nB;
    return String(a.idConta).localeCompare(String(b.idConta));
  });
  const filtrados = filtroPrograma ? itemsOrdenados.filter(i => String(i.idPrograma) === String(filtroPrograma)) : itemsOrdenados;
  const displayed = filtrados.slice(setPagination.startIndex, setPagination.startIndex + setPagination.rowsPerPage);

  const MESES = ['janeiro','fevereiro','marco','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
  const resumoPorPrograma = useMemo(() => {
    const map = {};
    (Array.isArray(items) ? items : []).forEach(it => {
      const chave = String(it.idPrograma);
      if (!map[chave]) map[chave] = { idPrograma: it.idPrograma, total: 0, qtd: 0 };
      map[chave].total += MESES.reduce((acc, m) => acc + (parseFloat(it[m]) || 0), 0);
      map[chave].qtd += 1;
    });
    return Object.values(map);
  }, [items]);

  useEffect(() => {
    if (!cnpj || !ano) { showDefaultModal('error', 'Escola ou ano não selecionado.'); setIsLoading(false); return; }
    if (!contas || contas.length === 0) { setIsLoading(true); return; }
    
    setIsLoading(true);
    
    axios.get(`${API_BASE_URL.RENDIMENTO}/all`, {
      params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
    })
      .then(async (resp) => {
        const rendimentos = resp.data || [];
        
        console.log('Contas disponíveis:', contas.length);
        console.log('Rendimentos carregados:', rendimentos.length);
        
        // Para cada conta que não tiver rendimento, salvar um vazio
        const contasSemRendimento = contas.filter(conta => 
          !rendimentos.find(r => r.idConta === conta.numConta)
        );
        
        console.log('Contas sem rendimento:', contasSemRendimento.length);
        
        if (contasSemRendimento.length > 0) {
          for (const conta of contasSemRendimento) {
            try {
              const payload = {
                ano: parseInt(ano),
                idPrograma: conta.idPrograma,
                idEscola: cnpj,
                idConta: conta.numConta,
                janeiro: 0.00, fevereiro: 0.00, marco: 0.00, abril: 0.00, maio: 0.00, junho: 0.00,
                julho: 0.00, agosto: 0.00, setembro: 0.00, outubro: 0.00, novembro: 0.00, dezembro: 0.00
              };
              await axios.post(`${API_BASE_URL.RENDIMENTO}/save`, payload, { headers: { Authorization: `Bearer ${token}` } });
              console.log('Rendimento criado para:', conta.numConta);
            } catch (err) {
              console.log('Erro ao criar rendimento para', conta.numConta, err.response?.data || err.message);
            }
          }
        }
        
        // Recarregar todos os rendimentos
        const finalResp = await axios.get(`${API_BASE_URL.RENDIMENTO}/all`, {
          params: { idEscola: cnpj, ano }, headers: { Authorization: `Bearer ${token}` }
        });
        setItems(finalResp.data || []);
      })
      .catch(err => {
        console.log('Erro geral:', err);
        if (err.response && err.response.status !== 200 && err.response.status !== 404) {
          showDefaultModal('error', 'Erro ao carregar rendimentos.');
        }
      })
      .finally(() => setIsLoading(false));
  }, [token, cnpj, ano, atualizar, contas]);

  function handleChange(e) { const { name, value } = e.target; setForm(f => ({ ...f, [name]: value })); }
  function handleNew() { setForm({ ...emptyForm, idEscola: cnpj || '', ano: ano || '', _isEditing: false }); setShowModal(true); }
  function handleEdit() { if (selected.length !== 1) return; setForm({ ...selected[0], _isEditing: true }); setShowModal(true); }

  function handleSave(e) {
    e.preventDefault();
    axios.post(`${API_BASE_URL.RENDIMENTO}/save`, form, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => {
        setShowModal(false);
        setAtualizar(a => !a);
        setSelected([]); // Limpa seleção após salvar
      })
      .catch(() => showDefaultModal('error', 'Erro ao salvar rendimento.'));
  }

  function handleDelete() {
    if (selected.length === 0) return;
    showConfirmModal('delete', () => Promise.all(selected.map(s => axios.post(`${API_BASE_URL.RENDIMENTO}/delete`, {
      ano: s.ano, idPrograma: s.idPrograma, idEscola: s.idEscola, idConta: s.idConta
    }, { headers: { Authorization: `Bearer ${token}` } })))
      .then(() => { setSelected([]); setAtualizar(a => !a); })
      .catch(() => showDefaultModal('error', 'Erro ao excluir rendimento.'))
    );
  }

  function handleImportJson(e) {
    const file = e.target.files[0];
    if (!file) return;
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = async (ev) => {
      let data;
      try { data = JSON.parse(ev.target.result); } catch { showDefaultModal('error', 'Arquivo JSON inválido.'); return; }
      const list = Array.isArray(data) ? data : [data];
      let saved = 0, errors = 0;
      for (const item of list) {
        const payload = {
          ano: parseInt(item.ano ?? item.year ?? ano),
          idPrograma: item.idPrograma ?? item.programa ?? item.program ?? '',
          idEscola: item.idEscola ?? item.escola ?? item.school ?? cnpj,
          idConta: item.idConta ?? item.conta ?? item.account ?? '',
          janeiro: parseFloat(item.janeiro ?? item.january ?? 0),
          fevereiro: parseFloat(item.fevereiro ?? item.february ?? 0),
          marco: parseFloat(item.marco ?? item.march ?? 0),
          abril: parseFloat(item.abril ?? item.april ?? 0),
          maio: parseFloat(item.maio ?? item.may ?? 0),
          junho: parseFloat(item.junho ?? item.june ?? 0),
          julho: parseFloat(item.julho ?? item.july ?? 0),
          agosto: parseFloat(item.agosto ?? item.august ?? 0),
          setembro: parseFloat(item.setembro ?? item.september ?? 0),
          outubro: parseFloat(item.outubro ?? item.october ?? 0),
          novembro: parseFloat(item.novembro ?? item.november ?? 0),
          dezembro: parseFloat(item.dezembro ?? item.december ?? 0),
        };
        try {
          await axios.post(`${API_BASE_URL.RENDIMENTO}/save`, payload, { headers: { Authorization: `Bearer ${token}` } });
          saved++;
        } catch { errors++; }
      }
      if (errors > 0) showDefaultModal('error', `Importação concluída com erros: ${saved} salvo(s), ${errors} erro(s).`);
      else showDefaultModal('success', `${saved} rendimento(s) importado(s) com sucesso.`);
      setAtualizar(a => !a);
    };
    reader.readAsText(file);
  }

  const meses = ['janeiro','fevereiro','marco','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];
  function total(item) { return meses.reduce((acc, m) => acc + (parseFloat(item[m]) || 0), 0).toFixed(2); }

  function keyOf(item) { return `${item.ano}_${item.idPrograma}_${item.idEscola}_${item.idConta}`; }
  function toggleSelect(item) {
    setSelected(prev => prev.find(s => keyOf(s) === keyOf(item)) ? [] : [item]);
  }

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="RENDIMENTOS" icon="bi-graph-up-arrow" />
        <PageContent>
          <Toolbar>
            {!isGestor && <button className="btn btn-info btn-sm botao-igual" onClick={() => setShowJsonUploadModal(true)}><i className="bi bi-file-arrow-down me-1"></i>Importar JSON</button>}
            {!isGestor && <button className="btn btn-warning btn-sm botao-igual" onClick={handleEdit} disabled={selected.length !== 1}><i className="bi bi-pencil me-1"></i>Editar</button>}
            {!isGestor && <button className="btn btn-danger btn-sm botao-igual centralizar-se-impar" onClick={handleDelete} disabled={selected.length === 0}><i className="bi bi-trash me-1"></i>Excluir</button>}
            <div className="input-group input-group-sm toolbar-search">
              <select className="form-select form-select-sm" value={filtroPrograma} onChange={e => setFiltroPrograma(e.target.value)}>
                <option value="">Filtrar por programa...</option>
                {programas.map(p => <option key={p.id} value={p.id}>{p.descricao}</option>)}
              </select>
            </div>
          </Toolbar>
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={String(prog.idPrograma)} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 170, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={programas.find(p => p.id === prog.idPrograma)?.descricao || prog.idPrograma}>{programas.find(p => p.id === prog.idPrograma)?.descricao || prog.idPrograma}</span>
                      <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                    </div>
                    <div className="d-flex justify-content-between fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{formatCurrency(prog.total)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <TableContainer isLoading={false}>
              <DataTable head={<tr>
                  <th style={{ width: '3rem' }}>#</th><th>Programa</th><th>Conta</th><th>Jan</th><th>Fev</th><th>Mar</th><th>Abr</th><th>Mai</th><th>Jun</th><th>Jul</th><th>Ago</th><th>Set</th><th>Out</th><th>Nov</th><th>Dez</th><th>Total</th>
                </tr>}>
                <tbody>
                  {displayed.map((item, idx) => (
                    <tr key={keyOf(item)} className={selected.find(s => keyOf(s) === keyOf(item)) ? 'table-success' : ''} onClick={() => toggleSelect(item)} style={{ cursor: 'pointer', fontSize: '0.75rem' }}>
                      <td>{setPagination.startIndex + idx + 1}</td><td>{programas?.find(p => p.id === item.idPrograma)?.descricao || item.idPrograma}</td><td>{item.idConta}</td>
                      {meses.map(m => {
                        const valor = item[m];
                        const isZero = parseFloat(valor) === 0;
                        return (
                          <td
                            key={m}
                            className="text-end"
                            style={isZero ? { backgroundColor: '#fff59d' } : {}}
                          >
                            {fmtCurrency(valor)}
                          </td>
                        );
                      })}
                      <td className={`align-middle text-end fw-bold ${total(item) < 0 ? 'text-danger' : 'text-success'}`} style={{ fontSize: '0.75rem' }}>{formatCurrency(total(item))}</td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          </TableContainer>
        </PageContent>
        <PaginationBar setPagination={setPagination} />
      </ResponsiveShadow>
      <RendimentoModal show={showModal} onClose={() => setShowModal(false)} onSubmit={handleSave} form={form} handleChange={handleChange} programas={programas} contas={contas} />
      <RendimentosJsonUploadModal show={showJsonUploadModal} onClose={() => setShowJsonUploadModal(false)} onSuccess={() => setAtualizar(a => !a)} contas={contas} />
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </Layout>
  );
}

export default Rendimentos;
